import { ActivityIndicator, Animated, StyleSheet } from "react-native";

import { AirPlayIcon, CastIcon } from "@/design/icons";
import { palette, space } from "@/design/tokens";
import { RemotePlaybackRouteChip } from "@/playback/RemotePlaybackHeroChrome";
import { useCastController } from "@/playback/cast/CastController";
import { usePlayerChromeOpacity } from "@/playback/usePlayerChromeVisibility";

export function HeroCastPill({
  visible,
  compact = false,
  airPlayActive = false,
  airPlayAvailable = false,
  previewAirPlay = false,
  onAirPlayPress,
  onInteractionStart,
  onInteractionEnd
}: {
  visible: boolean;
  compact?: boolean;
  airPlayActive?: boolean;
  airPlayAvailable?: boolean;
  previewAirPlay?: boolean;
  onAirPlayPress?: () => void;
  onAirPlayPickerOpened?: () => void;
  onAirPlayPickerClosed?: () => void;
  onInteractionStart?: () => void;
  onInteractionEnd?: () => void;
  fullscreen?: boolean;
}) {
  const cast = useCastController();
  const opacity = usePlayerChromeOpacity(visible);

  const airPlayCapable =
    previewAirPlay || airPlayActive || airPlayAvailable;
  if (!cast.castButtonVisible && !airPlayCapable) return null;
  const connecting = cast.castState === "connecting";
  const connected = cast.castState === "connected";
  const useAirPlay = airPlayCapable && !connected;
  const label = useAirPlay
    ? "AirPlay"
    : connecting
    ? "Connecting…"
    : connected
      ? cast.deviceName ?? "Connected TV"
      : "Cast to TV";

  return (
    <Animated.View
      onPointerEnter={onInteractionStart}
      onPointerLeave={onInteractionEnd}
      style={[
        styles.wrap,
        { right: compact ? space.lg : space.xl, top: compact ? space.lg : space.xl },
        { opacity, pointerEvents: visible ? "auto" : "none" }
      ]}
    >
      <RemotePlaybackRouteChip
        accessibilityLabel={
          useAirPlay
            ? airPlayActive || previewAirPlay
              ? "Playing with AirPlay. Choose or stop AirPlay"
              : "Choose an AirPlay device"
            : connected
            ? `Casting to ${cast.deviceName ?? "TV"}. Open Cast controls`
            : "Cast to TV"
        }
        active={connected || airPlayActive || previewAirPlay}
        disabled={connecting}
        icon={useAirPlay ? (
          <AirPlayIcon
            size={16}
            color={airPlayActive || previewAirPlay ? palette.accent : palette.text}
          />
        ) : connecting ? (
          <ActivityIndicator color={palette.text} size="small" />
        ) : (
          <CastIcon size={16} color={palette.text} />
        )}
        label={label}
        onInteractionStart={onInteractionStart}
        onInteractionEnd={onInteractionEnd}
        onPress={
          previewAirPlay
            ? undefined
            : useAirPlay
              ? onAirPlayPress
              : cast.showCastDialog
        }
      />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrap: { position: "absolute", zIndex: 40 }
});
